import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_BASE from '../config';
import usePageMeta from '../hooks/usePageMeta';

// Static fallback blog posts (shown when API is not yet available)
const STATIC_POSTS = [
    {
        id: 1,
        slug: 'top-interior-design-trends-2025',
        title: 'Top Interior Design Trends to Watch in 2025',
        excerpt: 'From biophilic design to earthy palettes and multifunctional furniture — here are the design trends that will define homes in 2025.',
        featured_image: 'https://images.unsplash.com/photo-1618219944342-824e40a13285?w=800&q=80',
        author: 'Priya Sharma',
        category: 'Design Trends',
        published_at: '2025-02-15',
        content: `<p>The interior design world is constantly evolving, and 2025 promises to be a year of bold experimentation balanced with a deep desire for comfort and sustainability. Here are the key trends our design team at Bliv Design Studio is excited about.</p>
    <h3>1. Biophilic Design</h3><p>Bringing nature indoors continues to grow stronger. Expect to see more indoor plants, natural materials like rattan, stone, and raw wood, and rooms designed to maximise natural light.</p>
    <h3>2. Earthy, Grounded Palettes</h3><p>Neutrals are evolving beyond beige. Terracottas, warm ochres, olive greens, and deep burgundy tones are creating rooms that feel grounded and timeless.</p>
    <h3>3. Multifunctional Spaces</h3><p>As hybrid work continues, rooms that serve double-duty — a guest room that doubles as a home office, a kitchen island that becomes a workspace — are in high demand.</p>`,
    },
    {
        id: 2,
        slug: 'modular-kitchen-guide-india',
        title: 'The Complete Guide to Modular Kitchens in India',
        excerpt: 'Everything you need to know about modular kitchen materials, layouts, costs, and how to choose the right design for your home.',
        featured_image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80',
        author: 'Ravi Teja',
        category: 'Modular Kitchen',
        published_at: '2025-01-28',
        content: `<p>A modular kitchen is the most significant investment in your kitchen renovation. Getting it right requires understanding materials, layouts, and what questions to ask your interior designer.</p>
    <h3>Choosing the Right Layout</h3><p>The best kitchen layout depends on your available space. L-shaped kitchens work well for medium spaces, U-shaped for larger kitchens, and straight-line kitchens are ideal for compact apartments.</p>
    <h3>Material Selection</h3><p>MDF, Plywood, and HDHMR are the go-to cabinet materials. For shutters, choose between laminates (budget-friendly), acrylic (premium look), or lacquered glass (ultra-premium). Always use kitchen-grade plywood for the base structure.</p>`,
    },
    {
        id: 3,
        slug: 'false-ceiling-designs-2025',
        title: 'False Ceiling Design Ideas That Will Transform Your Home',
        excerpt: 'False ceilings are no longer just functional — they are a design statement. Here are 8 stunning false ceiling designs to inspire your next renovation.',
        featured_image: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?w=800&q=80',
        author: 'Suresh Kumar',
        category: 'Design Ideas',
        published_at: '2025-01-10',
        content: `<p>A false ceiling (also called a suspended ceiling or dropped ceiling) can completely transform a room. It adds visual height, hides electrical wiring, and creates zones for different types of lighting.</p>
    <h3>POP vs Gypsum</h3><p>POP (Plaster of Paris) is more affordable and allows custom shapes but takes longer to install. Gypsum boards can be installed faster and are better for straight, geometric designs.</p>
    <h3>Cove Lighting</h3><p>Indirect cove lighting hidden within a false ceiling recess creates a warm, ambient glow that makes any room feel luxurious. This is one of Bliv Design Studio's most requested design features.</p>`,
    },
    {
        id: 4,
        slug: 'south-india-interior-design-cost-guide',
        title: 'Interior Design Cost Guide for Homes in South India (2025)',
        excerpt: 'How much does a complete home interior cost? We break down the costs for a 2BHK, 3BHK, and villa with transparent pricing.',
        featured_image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80',
        author: 'Priya Sharma',
        category: 'Budget & Cost',
        published_at: '2024-12-20',
        content: `<p>One of the most common questions we receive is "How much will a complete interior design cost?" The honest answer is: it depends. But we can give you realistic ballpark figures based on our 350+ projects across South India.</p>
    <h3>2BHK Interior Design Cost</h3><p>A complete 2BHK interior design (modular kitchen, 2 bedrooms with wardrobes, living room, false ceiling) typically costs between ₹8–14 lakhs depending on material quality and design complexity.</p>
    <h3>3BHK Interior Design Cost</h3><p>A complete 3BHK interior ranges from ₹12–22 lakhs. The key variable is the quality of materials chosen — HDHMR vs plywood, acrylic vs laminate shutters, Italian vs domestic tiles.</p>`,
    },
    {
        id: 5,
        slug: 'wardrobe-design-tips',
        title: '10 Wardrobe Design Tips to Maximise Your Storage',
        excerpt: 'Smart wardrobe organisation tips and design ideas to help you store more, stress less, and wake up to a perfectly organised bedroom every morning.',
        featured_image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
        author: 'Ravi Teja',
        category: 'Bedroom Design',
        published_at: '2024-12-05',
        content: `<p>A well-designed wardrobe is the backbone of an organised bedroom. Here are our top 10 tips from designing hundreds of wardrobes across South India.</p>
    <h3>1. Measure Your Clothes First</h3><p>Before designing, take stock of how many hanging items, folded items, and shoes you own. This directly determines how many hanging rails, shelves, and drawer units you need.</p>
    <h3>2. Sliding vs Hinged Doors</h3><p>Sliding doors are better for smaller rooms as they don't require clearance space to open. Hinged doors allow full visibility of the interior and are preferred for larger rooms.</p>`,
    },
    {
        id: 6,
        slug: 'vastu-interior-design',
        title: 'Vastu Shastra & Modern Interior Design — Finding the Balance',
        excerpt: 'How Bliv Design Studio integrates Vastu principles into contemporary home design without compromising on aesthetics or functionality.',
        featured_image: 'https://images.unsplash.com/photo-1600607688969-a5bfcd646154?w=800&q=80',
        author: 'Suresh Kumar',
        category: 'Design Philosophy',
        published_at: '2024-11-18',
        content: `<p>Vastu Shastra is an ancient Indian system of architecture that aligns living spaces with natural forces. Many of our clients request Vastu-compliant interior designs, and we have developed a thoughtful approach to integrating Vastu principles without making rooms look awkward or dated.</p>
    <h3>Vastu for the Kitchen</h3><p>Vastu recommends the kitchen in the South-East direction. The cooking platform should face East so the cook faces East while cooking. We integrate these principles through thoughtful layout planning.</p>`,
    },
];

function BlogCard({ post }) {
    const date = new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    return (
        <Link to={`/blog/${post.slug}`} className="block card-hover group">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-full">
                <div className="relative h-52 overflow-hidden">
                    <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-3 left-3 bg-[#2E63E8] text-white text-xs font-bold px-3 py-1 rounded-full">
                        {post.category}
                    </div>
                </div>
                <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                        <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {post.author}
                        </span>
                        <span>·</span>
                        <span>{date}</span>
                    </div>
                    <h3 className="text-gray-900 font-bold text-base leading-snug mb-2 group-hover:text-[#2E63E8] transition-colors line-clamp-2">
                        {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center gap-1 text-[#2E63E8] font-semibold text-sm mt-4 group-hover:gap-2 transition-all">
                        Read More <span>→</span>
                    </div>
                </div>
            </div>
        </Link>
    );
}

export default function BlogPage() {
    const [posts, setPosts] = useState(STATIC_POSTS);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('All');

    usePageMeta(
        'Interior Design Tips & Ideas | Bliv Design Studio Blog',
        'Read Bliv Design Studio articles on modular kitchen planning, wardrobe design, false ceiling ideas, colour combinations and material selection for Indian homes.'
    );

    useEffect(() => {
        fetch(`${API_BASE}/blog.php`)
            .then(r => r.json())
            .then(data => { if (data?.posts?.length) setPosts(data.posts); })
            .catch(() => {/* use static posts */ })
            .finally(() => setLoading(false));
    }, []);

    const categories = ['All', ...new Set(posts.map(p => p.category))];
    const filtered = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);

    return (
        <>
            {/* Hero */}
            <section className="relative pt-36 pb-24 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#1C2541] to-[#0D0D0D]" />
                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    <span className="inline-block bg-[#2E63E8]/20 border border-[#2E63E8]/40 text-[#2E63E8] text-xs font-semibold px-5 py-2 rounded-full mb-5 uppercase tracking-widest">
                        Design Insights
                    </span>
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-5">Our Blog</h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                        Expert interior design tips, trends, cost guides, and inspiration — straight from our design studio.
                    </p>
                </div>
            </section>

            {/* Category Filter */}
            <section className="bg-white border-b sticky top-[105px] z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex overflow-x-auto gap-2 py-3">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all
                  ${activeCategory === cat ? 'bg-[#2E63E8] text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Blog Grid */}
            <section className="section-pad bg-[#F8F9FA]">
                <div className="max-w-7xl mx-auto px-4">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                                    <div className="h-52 bg-gray-200" />
                                    <div className="p-5 space-y-3">
                                        <div className="h-4 bg-gray-200 rounded w-3/4" />
                                        <div className="h-3 bg-gray-200 rounded w-full" />
                                        <div className="h-3 bg-gray-200 rounded w-5/6" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            {/* Featured post */}
                            {activeCategory === 'All' && filtered[0] && (
                                <Link to={`/blog/${filtered[0].slug}`} className="block mb-10 group card-hover">
                                    <div className="bg-white rounded-3xl overflow-hidden shadow-md border border-gray-100">
                                        <div className="grid grid-cols-1 lg:grid-cols-2">
                                            <div className="h-64 lg:h-auto overflow-hidden relative">
                                                <img
                                                    src={filtered[0].featured_image}
                                                    alt={filtered[0].title}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                />
                                                <div className="absolute top-4 left-4 bg-[#2E63E8] text-black text-xs font-bold px-3 py-1 rounded-full">
                                                    FEATURED
                                                </div>
                                            </div>
                                            <div className="p-8 flex flex-col justify-center">
                                                <span className="text-[#2E63E8] font-semibold text-sm">{filtered[0].category}</span>
                                                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mt-2 mb-4 group-hover:text-[#2E63E8] transition-colors leading-snug">
                                                    {filtered[0].title}
                                                </h2>
                                                <p className="text-gray-500 leading-relaxed mb-5">{filtered[0].excerpt}</p>
                                                <div className="flex items-center gap-3 text-xs text-gray-400">
                                                    <span>{filtered[0].author}</span>
                                                    <span>·</span>
                                                    <span>{new Date(filtered[0].published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                                </div>
                                                <div className="flex items-center gap-1 text-[#2E63E8] font-semibold mt-5 group-hover:gap-2 transition-all">
                                                    Read Full Article <span>→</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                                {(activeCategory === 'All' ? filtered.slice(1) : filtered).map(post => (
                                    <BlogCard key={post.id} post={post} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
}
